import { useState } from 'react';
import { Search, Loader2, AlertTriangle, CheckCircle, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import GlowCard from '@/components/GlowCard';
import { cn } from '@/lib/utils';
import { useProfile } from '@/hooks/useProfile';
import { useToolUsage } from '@/hooks/useToolUsage';
import { toast } from 'sonner';
import { callTAMAI, parseAIJSON } from '@/lib/tamAI';

interface PlagiarismResult {
  score: number;
  analysis: string;
  flaggedSentences: string[];
  suggestions: string;
}

const FREE_WORD_LIMIT = 3000;
const PRO_WORD_LIMIT = 10000;
const MIN_WORDS = 50;

const PlagiarismChecker = () => {
  const { isPremium } = useProfile();
  useToolUsage('plagiarism_checker'); // Track tool usage

  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<PlagiarismResult | null>(null);
  const [copied, setCopied] = useState(false);

  const wordLimit = isPremium ? PRO_WORD_LIMIT : FREE_WORD_LIMIT;
  const wordCount = inputText.trim().split(/\s+/).filter(Boolean).length;

  const handleCheck = async () => {
    if (wordCount < MIN_WORDS) {
      toast.error(`Please enter at least ${MIN_WORDS} words for accurate analysis`);
      return;
    }

    if (wordCount > wordLimit) {
      toast.error(`Text exceeds ${wordLimit.toLocaleString()} word limit`);
      return;
    }

    setIsLoading(true);
    setResult(null);

    const prompt = `Analyze this text for potential plagiarism and originality.

Text to analyze:
"${inputText}"

Provide a JSON response with:
1. "score": originality percentage (0-100)
2. "analysis": brief analysis of the text's originality
3. "flaggedSentences": array of sentences that seem generic or potentially copied
4. "suggestions": how to improve originality

Respond ONLY with valid JSON, no markdown or code blocks.`;

    try {
      const response = await callTAMAI(prompt, { temperature: 0.3, maxTokens: 1024 });
      
      // Parse JSON from response using shared utility
      const parsed = parseAIJSON<PlagiarismResult>(response);
      setResult(parsed);
      toast.success('Analysis complete!');
    } catch (error) {
      console.error('Plagiarism check error:', error);
      toast.error('Analysis failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-500/10 border-green-500/30';
    if (score >= 50) return 'bg-yellow-500/10 border-yellow-500/30';
    return 'bg-red-500/10 border-red-500/30';
  };

  const handleCopy = async () => {
    if (!result) return;
    const text = `Originality Score: ${result.score}%\n\nAnalysis: ${result.analysis}\n\nSuggestions: ${result.suggestions}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Report copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-primary/10">
            <Search className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Plagiarism Checker</h1>
            <p className="text-muted-foreground">Check your text for originality • Powered by TAM API</p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">
          {isPremium ? 'Pro: 10K words' : 'Free: 3K words'}
        </Badge>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input */}
        <GlowCard className="card-lift">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Text to Check</h2>
            <Badge variant={wordCount < MIN_WORDS ? 'destructive' : wordCount > wordLimit ? 'destructive' : 'secondary'} className="text-xs">
              {wordCount}/{wordLimit.toLocaleString()} words
            </Badge>
          </div>

          <Textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Paste your text to check for plagiarism... (minimum 50 words)"
            className="min-h-[300px] resize-none bg-muted/30"
          />

          <Progress
            value={(wordCount / wordLimit) * 100}
            className="mt-2 h-1"
          />

          <Button
            onClick={handleCheck}
            disabled={isLoading || wordCount < MIN_WORDS}
            className="w-full mt-4 hover-bounce ripple"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing text...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Check for Plagiarism
              </>
            )}
          </Button>

          {wordCount < MIN_WORDS && wordCount > 0 && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {MIN_WORDS - wordCount} more words needed for analysis
            </p>
          )}
        </GlowCard>

        {/* Result */}
        <GlowCard className="card-lift">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Analysis Results</h2>
            {result && (
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative">
                <Search className="h-12 w-12 text-primary animate-pulse" />
                <div className="absolute inset-0 rounded-full border-4 border-primary/20 animate-ping" />
              </div>
              <p className="mt-4 text-muted-foreground">Analyzing your text...</p>
              <Progress value={66} className="w-48 mt-4 h-2" />
            </div>
          ) : result ? (
            <div className="space-y-4 animate-fade-in">
              {/* Score Circle */}
              <div className={cn("p-6 rounded-xl text-center border", getScoreBg(result.score))}>
                <div className="relative inline-flex items-center justify-center">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-muted/30"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={352}
                      strokeDashoffset={352 - (352 * result.score) / 100}
                      className={cn("transition-all duration-1000", getScoreColor(result.score))}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={cn("text-3xl font-bold", getScoreColor(result.score))}>
                      {result.score}%
                    </span>
                    <span className="text-xs text-muted-foreground">Original</span>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-center gap-2">
                  {result.score >= 80 ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="font-medium text-green-500">Likely Original</span>
                    </>
                  ) : result.score >= 50 ? (
                    <>
                      <AlertTriangle className="h-5 w-5 text-yellow-500" />
                      <span className="font-medium text-yellow-500">Needs Review</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <span className="font-medium text-red-500">Potential Issues</span>
                    </>
                  )}
                </div>
              </div>

              {/* Analysis */}
              <div className="p-4 rounded-lg bg-muted/30">
                <h3 className="font-medium mb-2">Analysis</h3>
                <p className="text-sm text-muted-foreground">{result.analysis}</p>
              </div>

              {/* Flagged Sentences */}
              {result.flaggedSentences && result.flaggedSentences.length > 0 && (
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                  <h3 className="font-medium mb-2 text-yellow-500">Flagged Sentences</h3>
                  <ul className="space-y-2">
                    {result.flaggedSentences.map((sentence, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex gap-2">
                        <span className="text-yellow-500">•</span>
                        {sentence}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Suggestions */}
              {result.suggestions && (
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <h3 className="font-medium mb-2">Suggestions</h3>
                  <p className="text-sm text-muted-foreground">{result.suggestions}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Search className="h-12 w-12 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">Results will appear here</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Enter at least 50 words to check</p>
            </div>
          )}
        </GlowCard>
      </div>
    </div>
  );
};

export default PlagiarismChecker;
